// Admin CRUD справочников — /api/admin/rejection-reasons и /api/admin/currencies (V16 W11).

import { apiClient } from './client';

/** Admin-view причины отказа (включая inactive). */
export interface AdminRejectionReason {
  /** UUID причины. */
  id: string;
  /** Название. */
  name: string;
  /** Доступна ли в каталоге форм. */
  active: boolean;
  /** Момент создания (ISO-8601). */
  createdAt: string;
  /** Момент изменения (ISO-8601). */
  updatedAt: string;
}

/** Тело POST причины: только имя. */
export interface RejectionReasonCreateRequest {
  /** Название причины. */
  name: string;
}

/** Тело PUT причины: имя и активность. */
export interface RejectionReasonUpdateRequest {
  /** Название причины. */
  name: string;
  /** Доступна ли в каталоге форм. */
  active: boolean;
}

/** Admin-view валюты (включая inactive). */
export interface AdminCurrency {
  /** ISO-3 код (PK). */
  code: string;
  /** Отображаемый символ. */
  symbol: string;
  /** Доступна ли в каталоге форм. */
  active: boolean;
  /** Момент создания (ISO-8601). */
  createdAt: string;
  /** Момент изменения (ISO-8601). */
  updatedAt: string;
}

/** Тело POST валюты: код и символ. */
export interface CurrencyCreateRequest {
  /** ISO-3 код (`^[A-Z]{3}$`). */
  code: string;
  /** Отображаемый символ (≤8). */
  symbol: string;
}

/** Тело PUT валюты: символ и активность; код в пути не меняется. */
export interface CurrencyUpdateRequest {
  /** Отображаемый символ. */
  symbol: string;
  /** false при in-use → 409 `dictionary_in_use`. */
  active: boolean;
}

/** API admin CRUD справочников причин и валют. */
export const adminDictionariesApi = {
  /** GET /api/admin/rejection-reasons → все причины, включая inactive. */
  async listRejectionReasons(): Promise<AdminRejectionReason[]> {
    const { data } = await apiClient.get<AdminRejectionReason[]>('/api/admin/rejection-reasons');
    return data;
  },

  /** POST /api/admin/rejection-reasons `{name}` → 201. */
  async createRejectionReason(payload: RejectionReasonCreateRequest): Promise<AdminRejectionReason> {
    const { data } = await apiClient.post<AdminRejectionReason>(
      '/api/admin/rejection-reasons',
      payload,
    );
    return data;
  },

  /** PUT /api/admin/rejection-reasons/{id} `{name, active}`. */
  async updateRejectionReason(
    id: string,
    payload: RejectionReasonUpdateRequest,
  ): Promise<AdminRejectionReason> {
    const { data } = await apiClient.put<AdminRejectionReason>(
      `/api/admin/rejection-reasons/${id}`,
      payload,
    );
    return data;
  },

  /** DELETE /api/admin/rejection-reasons/{id} → 204 или 409 `dictionary_in_use`. */
  async deleteRejectionReason(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/rejection-reasons/${id}`);
  },

  /** GET /api/admin/currencies → все валюты, включая inactive. */
  async listCurrencies(): Promise<AdminCurrency[]> {
    const { data } = await apiClient.get<AdminCurrency[]>('/api/admin/currencies');
    return data;
  },

  /** POST /api/admin/currencies `{code, symbol}` → 201. */
  async createCurrency(payload: CurrencyCreateRequest): Promise<AdminCurrency> {
    const { data } = await apiClient.post<AdminCurrency>('/api/admin/currencies', payload);
    return data;
  },

  /** PUT /api/admin/currencies/{code} `{symbol, active}`; code не менять. */
  async updateCurrency(code: string, payload: CurrencyUpdateRequest): Promise<AdminCurrency> {
    const { data } = await apiClient.put<AdminCurrency>(`/api/admin/currencies/${code}`, payload);
    return data;
  },

  /** DELETE /api/admin/currencies/{code} → 204 или 409 `dictionary_in_use`. */
  async deleteCurrency(code: string): Promise<void> {
    await apiClient.delete(`/api/admin/currencies/${code}`);
  },
};
